import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { scrollToId } from "@/pages/LandingPage";

const EASE = [0.22, 1, 0.36, 1];

const PLANS = [
  {
    name: "Basic",
    price: "39.95",
    tagline: "Everyday legal cover for you and your spouse.",
    features: [
      "Unlimited advice on personal legal issues",
      "Will preparation & power of attorney",
      "Traffic ticket representation (1/yr)",
      "Civil trial defense hours",
      "Document review up to 15 pages",
      "24/7 emergency access for covered situations",
    ],
    featured: false,
    testid: "plan-basic",
  },
  {
    name: "Advanced",
    price: "49.95",
    tagline: "The family favorite — covers you, your spouse, and eligible kids.",
    features: [
      "Everything in Basic",
      "Annual updates to your will",
      "Uncontested divorce, adoption & prenups",
      "Tenant lease & mortgage document review",
      "Driver's license reinstatement help",
      "Document review up to 25 pages",
    ],
    featured: true,
    testid: "plan-advanced",
  },
  {
    name: "Premium",
    price: "59.95",
    tagline: "The most complete protection, including estate matters.",
    features: [
      "Everything in Advanced",
      "Revocable trust preparation",
      "IRS audit representation",
      "Simplified probate assistance",
      "25% off services beyond the plan",
      "Document review up to 35 pages",
    ],
    featured: false,
    testid: "plan-premium",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.14, duration: 0.8, ease: EASE } }),
};

export default function Plans() {
  return (
    <section id="plans" className="bg-white py-24 md:py-36 scroll-mt-20" data-testid="plans-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={0}
          className="font-mono-accent text-xs uppercase tracking-[0.28em] text-blue-600 mb-6">
          Plans at a glance
        </motion.p>
        <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={1}
          className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B192C] max-w-3xl leading-[1.1] mb-6">
          Three plans. Real prices. No surprises.
        </motion.h2>
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={2}
          className="text-lg text-slate-600 leading-relaxed max-w-2xl mb-16">
          This is the part most pages hide behind a form. Here are the actual personal plan tiers —
          pick a starting point and I'll send the full coverage documents.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i + 1}
              whileHover={{ y: -6 }}
              className={`relative rounded-3xl p-8 md:p-10 flex flex-col border transition-shadow duration-300 ${
                plan.featured
                  ? "bg-[#0B192C] border-[#0B192C] text-white shadow-2xl shadow-blue-950/30"
                  : "bg-[#F8FAFC] border-slate-200 hover:shadow-lg hover:shadow-blue-100"
              }`}
              data-testid={plan.testid}
            >
              {plan.featured && (
                <span className="absolute -top-3.5 left-8 bg-sky-400 text-[#0B192C] font-mono-accent text-[10px] uppercase tracking-[0.2em] font-semibold px-4 py-1.5 rounded-full">
                  Most popular
                </span>
              )}
              <h3 className={`font-display font-bold text-2xl tracking-tight ${plan.featured ? "text-white" : "text-[#0B192C]"}`}>
                {plan.name}
              </h3>
              <p className={`text-sm mt-2 mb-7 leading-relaxed ${plan.featured ? "text-slate-300" : "text-slate-500"}`}>
                {plan.tagline}
              </p>
              <p className="mb-8">
                <span className={`font-display font-extrabold text-5xl tracking-tight ${plan.featured ? "text-sky-400" : "text-blue-700"}`}>
                  ${plan.price}
                </span>
                <span className={`text-sm ${plan.featured ? "text-slate-400" : "text-slate-500"}`}> /mo</span>
              </p>
              <ul className="space-y-3.5 flex-1 mb-9">
                {plan.features.map((f) => (
                  <li key={f} className={`flex items-start gap-3 text-sm leading-relaxed ${plan.featured ? "text-slate-200" : "text-slate-700"}`}>
                    <Check size={16} className={`mt-0.5 shrink-0 ${plan.featured ? "text-sky-400" : "text-blue-600"}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`${plan.testid}-cta`}
                onClick={() => scrollToId("#get-started")}
                className={`group w-full flex items-center justify-center gap-2 font-semibold py-3.5 rounded-full transition-colors duration-300 ${
                  plan.featured
                    ? "bg-sky-400 hover:bg-sky-300 text-[#0B192C]"
                    : "bg-white border border-slate-300 text-[#0B192C] hover:border-blue-500 hover:text-blue-700"
                }`}
              >
                Ask John about {plan.name}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.div>
          ))}
        </div>

        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
          className="text-xs text-slate-400 leading-relaxed mt-10 max-w-3xl" data-testid="plans-disclaimer">
          Summarized highlights of the current personal plans — full coverage details, exclusions,
          and any waiting periods are in the official plan documents, which John provides before you
          enroll. Business plans are priced separately by company size; ask for a quote.
        </motion.p>
      </div>
    </section>
  );
}
